import React, { forwardRef, useEffect, useRef, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import { FaTimes } from 'react-icons/fa'
import { LuBell } from 'react-icons/lu'
import { MdDone } from 'react-icons/md'
import { getNotificationColor, getNotificationIcon } from '@helpers/NotificationHelpers'
import NotificationItem from '@components/NotificationItem/NotificationItem'
import * as $Notification from '@services/Notification'
import INotification from '@interfaces/INotification'
import './NotificationBell.css'

const BellToggle = forwardRef(({ children, onClick }: any, ref: any) => (
  <button
    ref={ref}
    onClick={(e) => {
      e.preventDefault()
      onClick(e)
    }}
    className="notification-bell-toggle"
  >
    {children}
  </button>
))

BellToggle.displayName = 'BellToggle'

const INITIAL_VISIBLE_COUNT = 5

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<INotification[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchNotifications = async () => {
    try {
      const { data: { data } } = await $Notification.getAll()
      setNotifications(data)
    } catch {
      // silently ignore — polling will retry
    }
  }

  useEffect(() => {
    fetchNotifications()
    intervalRef.current = setInterval(fetchNotifications, 60_000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  const handleMarkAllRead = async () => {
    try {
      await $Notification.markAllRead()
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    } catch {
      // silently ignore
    }
  }

  const handleMarkRead = async (id: string) => {
    try {
      await $Notification.markRead(id)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
    } catch {
      // silently ignore
    }
  }

  const orderColorMap = new Map<string, number>()
  notifications.forEach(n => {
    if (!orderColorMap.has(n.order_internal_id)) {
      orderColorMap.set(n.order_internal_id, orderColorMap.size)
    }
  })

  const unreadCount = notifications.filter(n => !n.is_read).length

  const handleToggle = (nextShow: boolean) => {
    setShowDropdown(nextShow)
    if (!nextShow) setShowAll(false)
  }

  return (
    <li className="dropdown dropdown-list-toggle">
      <Dropdown className="beep" show={showDropdown} onToggle={handleToggle}>
        <Dropdown.Toggle as={BellToggle} variant="primary">
          <LuBell size={22} />
          {unreadCount > 0 && <span className="badge badge-pink notifications-badge">{unreadCount}</span>}
        </Dropdown.Toggle>

        <Dropdown.Menu align="end" className="notification-dropdown-menu" style={{ maxHeight: '550px', padding: 0, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', overflow: 'hidden' }} renderOnMount>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8f9fa',
            flexShrink: 0,
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#e7f3ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              >
                <LuBell size={18} color="#4d61aa" />
              </div>
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#2c3e50' }}>Notificações</div>
                <div style={{ fontSize: '13px', color: '#6c757d' }}>{unreadCount} não lidas</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                className="notification-mark-all-btn"
                onClick={handleMarkAllRead}
              >
                <MdDone size={16} /> Marcar como lidas
              </button>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                }}
                onClick={() => setShowDropdown(false)}
              >
                <FaTimes size={16} />
              </button>
            </div>
          </div>

          <div style={{ padding: 0, paddingBottom: '4px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#adb5bd' }}>
                <LuBell size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
                <div style={{ fontSize: '14px', fontWeight: '500' }}>Nenhuma notificação</div>
              </div>
            ) : (
              <>
                {(showAll ? notifications : notifications.slice(0, INITIAL_VISIBLE_COUNT)).map(n => (
                  <NotificationItem
                    key={n.id}
                    title={n.order_internal_id}
                    subtitle={n.subtitle}
                    datetime={n.created_at}
                    description={n.description}
                    isRead={n.is_read}
                    icon={getNotificationIcon(n.event_type)}
                    color={getNotificationColor(orderColorMap.get(n.order_internal_id) ?? 0)}
                    onRead={() => handleMarkRead(n.id)}
                  />
                ))}
                {notifications.length > INITIAL_VISIBLE_COUNT && (
                  <button
                    className="notification-show-more-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowAll(prev => !prev)
                    }}
                  >
                    {showAll ? 'Ver menos' : `Ver mais (${notifications.length - INITIAL_VISIBLE_COUNT} restantes)`}
                  </button>
                )}
              </>
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </li>
  )
}

export default NotificationBell
